import { useServerData, LIST_MIN_LOADING_MS } from '@/hooks/useServerData';
import {
  DashboardListsService,
  type DashboardFilterOption,
  type DashboardItineraryCounts,
  type DashboardPage,
  type DashboardTravelRequestCounts,
  type DashboardTravelRequestPage,
} from '@/services/dashboard-lists.service';
import type { DashboardItinerary, DashboardTravelRequest } from '@/lib/models/dashboard';
import type {
  DashboardItinerariesQuery,
  DashboardItineraryTab,
  DashboardTravelRequestFilter,
  DashboardTravelRequestsQuery,
} from '@/integrations/supabase/rpc-types';

const EMPTY_PAGE = { rows: [], totalCount: 0 };

// ── Itinerari ────────────────────────────────────────────────────────────

/** Una pagina di una tab della dashboard viaggi. */
export const useDashboardItinerariesPage = (query: DashboardItinerariesQuery, refreshKey: number) =>
  useServerData<DashboardPage<DashboardItinerary>>(
    JSON.stringify({ query, refreshKey }),
    EMPTY_PAGE,
    () => DashboardListsService.getItinerariesPage(query),
    true,
    LIST_MIN_LOADING_MS
  );

/**
 * Contatori per tab e sotto-stato. Senza filtri sono i badge della pagina; con
 * consulente/cliente le pillole di stato della tab aperta.
 */
export const useDashboardItineraryCounts = (
  filters: { designerId?: string | null; clientEmail?: string | null },
  refreshKey: number,
  enabled = true
) =>
  useServerData<DashboardItineraryCounts>(
    JSON.stringify({ filters, refreshKey }),
    { byTab: {}, byPhase: {} },
    () => DashboardListsService.getItineraryCounts(filters),
    enabled
  );

/** Consulenti e clienti presenti in una tab, per i due menu filtro. */
export const useDashboardItineraryFilterOptions = (tab: DashboardItineraryTab, refreshKey: number, enabled = true) =>
  useServerData<{ designers: DashboardFilterOption[]; clients: DashboardFilterOption[] }>(
    JSON.stringify({ tab, refreshKey }),
    { designers: [], clients: [] },
    () => DashboardListsService.getItineraryFilterOptions(tab),
    enabled
  );

// ── Travel request ───────────────────────────────────────────────────────

/** Una pagina di una vista della pagina richieste. */
export const useDashboardTravelRequestsPage = (query: DashboardTravelRequestsQuery, refreshKey: number) =>
  useServerData<DashboardTravelRequestPage>(
    JSON.stringify({ query, refreshKey }),
    { ...EMPTY_PAGE, rows: [] as DashboardTravelRequest[], newCount: 0 },
    () => DashboardListsService.getTravelRequestsPage(query),
    true,
    LIST_MIN_LOADING_MS
  );

/**
 * Contatori per vista e sotto-stato. Senza filtri sono i badge della pagina; con i filtri
 * della toolbar le pillole di stato della vista active.
 */
export const useDashboardTravelRequestCounts = (
  refreshKey: number,
  filters: DashboardTravelRequestFilter[] = [],
  enabled = true
) =>
  useServerData<DashboardTravelRequestCounts>(
    JSON.stringify({ filters, refreshKey }),
    { byView: {}, byPhase: {} },
    () => DashboardListsService.getTravelRequestCounts(filters),
    enabled
  );

/**
 * Una richiesta per il deep link `?requestId=`: puo' stare in qualunque vista e pagina,
 * quindi la si chiede per id. `resolvedId` dice a quale id si riferisce la risposta
 * (`request` null = non esiste o non e' visibile).
 */
export const useDashboardTravelRequest = (requestId: string | null) =>
  useServerData<{ resolvedId: string | null; request: DashboardTravelRequest | null }>(
    JSON.stringify({ requestId }),
    { resolvedId: null, request: null },
    async () => ({
      resolvedId: requestId,
      request: requestId ? await DashboardListsService.getTravelRequest(requestId) : null,
    }),
    !!requestId
  );
